import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader } from '../components/shared/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Separator } from '../components/ui/separator';
import { StatusBadge } from '../components/shared/StatusBadge';
import { UrgencyBadge } from '../components/shared/UrgencyBadge';
import { PdfPreviewModal } from '../components/shared/PdfPreviewModal';
import { useAppContext } from '../lib/store';
import { Edit, FileDown, Copy, Mail, ArrowLeft, Clock, User } from 'lucide-react';
import { toast } from 'sonner';
export function WorkOrderDetailPage() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const {
    workOrders,
    customers,
    users,
    duplicateWorkOrder
  } = useAppContext();
  const [showPdf, setShowPdf] = useState(false);
  const workOrder = workOrders.find(wo => wo.id === id);
  if (!workOrder) {
    return <div data-id="element-429">Delovni nalog ne obstaja.</div>;
  }
  const customer = customers.find(c => c.id === workOrder.customer_id);
  const assignedUser = users.find(u => u.id === workOrder.assigned_user_id);
  const createdBy = users.find(u => u.id === workOrder.created_by);
  const updatedBy = users.find(u => u.id === workOrder.updated_by);
  const handleDuplicate = () => {
    duplicateWorkOrder(workOrder.id);
    navigate('/nalogi');
  };
  const handleEmail = () => {
    toast.success('Pripravljam e-poštno sporočilo s pripetim PDF dokumentom...');
  };
  return <div className="max-w-5xl mx-auto space-y-6 pb-12" data-id="element-430">
      <div className="flex items-center gap-2 mb-2" data-id="element-431">
        <Button variant="ghost" size="sm" onClick={() => navigate('/nalogi')} className="text-slate-500" data-id="element-432">
          <ArrowLeft className="w-4 h-4 mr-1" data-id="element-433" /> Nazaj na seznam
        </Button>
      </div>

      <PageHeader title={`Delovni nalog ${workOrder.work_order_number}`} description={`Ustvarjeno ${new Date(workOrder.created_at).toLocaleDateString('sl-SI')}`} data-id="element-434">
        <div className="flex flex-wrap gap-2" data-id="element-435">
          <Button variant="outline" onClick={() => navigate(`/nalogi/${id}/uredi`)} data-id="element-436">
            <Edit className="w-4 h-4 mr-2" data-id="element-437" /> Uredi
          </Button>
          <Button variant="outline" onClick={() => setShowPdf(true)} data-id="element-438">
            <FileDown className="w-4 h-4 mr-2" data-id="element-439" /> PDF
          </Button>
        </div>
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-440">
        {/* Main Content */}
        <div className="md:col-span-2 space-y-6" data-id="element-441">
          <Card data-id="element-442">
            <CardHeader className="pb-3" data-id="element-443">
              <CardTitle className="text-lg" data-id="element-444">Podatki o nalogu</CardTitle>
            </CardHeader>
            <CardContent data-id="element-445">
              <div className="grid grid-cols-2 gap-y-4 gap-x-8 text-sm" data-id="element-446">
                <div data-id="element-447">
                  <p className="text-slate-500 mb-1" data-id="element-448">Stranka</p>
                  <p className="font-medium" data-id="element-449">{customer?.name || '/'}</p>
                </div>
                <div data-id="element-450">
                  <p className="text-slate-500 mb-1" data-id="element-451">Datum izvedbe</p>
                  <p className="font-medium" data-id="element-452">
                    {new Date(workOrder.execution_date).toLocaleDateString('sl-SI')}
                  </p>
                </div>
                <div className="col-span-2" data-id="element-453">
                  <p className="text-slate-500 mb-1" data-id="element-454">Lokacija</p>
                  <p className="font-medium" data-id="element-455">{workOrder.location}</p>
                </div>
                <div data-id="element-456">
                  <p className="text-slate-500 mb-1" data-id="element-457">Št. / Naročnik</p>
                  <p className="font-medium" data-id="element-458">
                    {workOrder.customer_reference_or_orderer || '/'}
                  </p>
                </div>
                <div data-id="element-459">
                  <p className="text-slate-500 mb-1" data-id="element-460">Izvajalec</p>
                  <p className="font-medium" data-id="element-461">
                    {assignedUser?.full_name || '/'}
                  </p>
                </div>
              </div>

              {workOrder.work_description && <div className="mt-6 pt-6 border-t" data-id="element-462">
                  <p className="text-slate-500 mb-2 text-sm" data-id="element-463">Opis opravila</p>
                  <p className="whitespace-pre-wrap text-sm" data-id="element-464">
                    {workOrder.work_description}
                  </p>
                </div>}
            </CardContent>
          </Card>

          {/* Labor */}
          <Card data-id="element-465">
            <CardHeader className="pb-3" data-id="element-466">
              <CardTitle className="text-lg" data-id="element-467">Opravljeno delo</CardTitle>
            </CardHeader>
            <CardContent data-id="element-468">
              {workOrder.labor_items.length > 0 ? <Table data-id="element-469">
                  <TableHeader data-id="element-470">
                    <TableRow data-id="element-471">
                      <TableHead data-id="element-472">Opis</TableHead>
                      <TableHead className="text-right w-24" data-id="element-473">
                        Količina
                      </TableHead>
                      <TableHead className="w-24" data-id="element-474">EM</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody data-id="element-475">
                    {workOrder.labor_items.map(item => <TableRow key={item.id} data-id="element-476">
                        <TableCell data-id="element-477">{item.description}</TableCell>
                        <TableCell className="text-right" data-id="element-478">
                          {item.quantity}
                        </TableCell>
                        <TableCell data-id="element-479">{item.unit}</TableCell>
                      </TableRow>)}
                  </TableBody>
                </Table> : <p className="text-sm text-slate-500 italic" data-id="element-480">
                  Ni vnesenega dela.
                </p>}
            </CardContent>
          </Card>

          {/* Material */}
          <Card data-id="element-481">
            <CardHeader className="pb-3" data-id="element-482">
              <CardTitle className="text-lg" data-id="element-483">Porabljen material</CardTitle>
            </CardHeader>
            <CardContent data-id="element-484">
              {workOrder.material_items.length > 0 ? <Table data-id="element-485">
                  <TableHeader data-id="element-486">
                    <TableRow data-id="element-487">
                      <TableHead data-id="element-488">Naziv materiala</TableHead>
                      <TableHead className="text-right w-24" data-id="element-489">
                        Količina
                      </TableHead>
                      <TableHead className="w-24" data-id="element-490">EM</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody data-id="element-491">
                    {workOrder.material_items.map(item => <TableRow key={item.id} data-id="element-492">
                        <TableCell data-id="element-493">{item.material_name}</TableCell>
                        <TableCell className="text-right" data-id="element-494">
                          {item.quantity}
                        </TableCell>
                        <TableCell data-id="element-495">{item.unit}</TableCell>
                      </TableRow>)}
                  </TableBody>
                </Table> : <p className="text-sm text-slate-500 italic" data-id="element-496">
                  Ni vnesenega materiala.
                </p>}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6" data-id="element-497">
          <Card data-id="element-498">
            <CardHeader className="pb-3" data-id="element-499">
              <CardTitle className="text-lg" data-id="element-500">Status</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4" data-id="element-501">
              <div data-id="element-502">
                <p className="text-sm text-slate-500 mb-2" data-id="element-503">Trenutni status</p>
                <StatusBadge status={workOrder.status} data-id="element-504" />
              </div>
              <Separator data-id="element-505" />
              <div data-id="element-506">
                <p className="text-sm text-slate-500 mb-2" data-id="element-507">Nujnost</p>
                <UrgencyBadge urgency={workOrder.urgency} data-id="element-508" />
              </div>
            </CardContent>
          </Card>

          <Card data-id="element-509">
            <CardHeader className="pb-3" data-id="element-510">
              <CardTitle className="text-lg" data-id="element-511">Podpisi in opombe</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4" data-id="element-512">
              <div data-id="element-513">
                <p className="text-sm text-slate-500 mb-1" data-id="element-514">Izvajalec</p>
                <p className="font-medium text-sm" data-id="element-515">
                  {workOrder.technician_name || 'Ni podpisa'}
                </p>
              </div>
              <div data-id="element-516">
                <p className="text-sm text-slate-500 mb-1" data-id="element-517">Naročnik</p>
                <p className="font-medium text-sm" data-id="element-518">
                  {workOrder.orderer_name || 'Ni podpisa'}
                </p>
              </div>

              {workOrder.notes && <>
                  <Separator data-id="element-519" />
                  <div data-id="element-520">
                    <p className="text-sm text-slate-500 mb-1" data-id="element-521">
                      Interne opombe
                    </p>
                    <p className="text-sm whitespace-pre-wrap" data-id="element-522">
                      {workOrder.notes}
                    </p>
                  </div>
                </>}
            </CardContent>
          </Card>

          <Card data-id="element-523">
            <CardHeader className="pb-3" data-id="element-524">
              <CardTitle className="text-lg" data-id="element-525">Zgodovina</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm" data-id="element-526">
              <div className="flex gap-3" data-id="element-527">
                <Clock className="w-4 h-4 text-slate-400 mt-0.5" data-id="element-528" />
                <div data-id="element-529">
                  <p className="font-medium" data-id="element-530">Ustvarjeno</p>
                  <p className="text-slate-500" data-id="element-531">
                    {new Date(workOrder.created_at).toLocaleString('sl-SI')}
                  </p>
                  <p className="text-slate-500" data-id="element-532">{createdBy?.full_name}</p>
                </div>
              </div>
              <div className="flex gap-3" data-id="element-533">
                <User className="w-4 h-4 text-slate-400 mt-0.5" data-id="element-534" />
                <div data-id="element-535">
                  <p className="font-medium" data-id="element-536">Zadnja sprememba</p>
                  <p className="text-slate-500" data-id="element-537">
                    {new Date(workOrder.updated_at).toLocaleString('sl-SI')}
                  </p>
                  <p className="text-slate-500" data-id="element-538">{updatedBy?.full_name}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <PdfPreviewModal open={showPdf} onOpenChange={setShowPdf} workOrder={workOrder} data-id="element-539" />
    </div>;
}